import { Button, Group, Textarea } from '@chakra-ui/react';
import React, { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { FiArrowUp } from 'react-icons/fi';

export interface ChatComposerRowProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void | Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** When true (default), Enter submits; Shift+Enter inserts newline. */
  submitOnEnter?: boolean;
  /** Root element for focus management (e.g. query `textarea[data-chat-composer-text]`) */
  rootRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * When true, textarea height follows content (no fixed max height).
   * Other call sites keep the compact fixed cap.
   */
  autoGrow?: boolean;
  /** Rendered between the textarea and the model picker / submit button (e.g. thread picker). */
  trailingActions?: ReactNode;
  /**
   * Model or settings control placed before the submit button. Keeps the AI model choice
   * visible whenever the row drives an AI-backed action.
   */
  modelPicker?: ReactNode;
  /** Overrides default "disable when textarea is blank" for the submit button only */
  submitDisabled?: boolean;
  submitAriaLabel?: string;
  submitTitle?: string;
  submitIcon?: ReactNode;
}

/**
 * Compact chat-style row: textarea + blue primary action (up arrow). Shared across dashboard,
 * document messages, AI audit modal, document prompt footer, and document AI chat.
 */
export function ChatComposerRow({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  loading = false,
  submitOnEnter = true,
  rootRef,
  autoGrow = false,
  trailingActions,
  modelPicker,
  submitDisabled,
  submitAriaLabel,
  submitTitle,
  submitIcon,
}: ChatComposerRowProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const gridTemplateColumns = useMemo(() => {
    const parts = ['1fr'];
    if (trailingActions) parts.push('auto');
    if (modelPicker) parts.push('auto');
    parts.push('auto');
    return parts.join(' ');
  }, [trailingActions, modelPicker]);

  useLayoutEffect(() => {
    if (!autoGrow) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!submitOnEnter) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  const submitButtonDisabled =
    submitDisabled !== undefined
      ? submitDisabled || disabled || loading
      : !value.trim() || disabled || loading;

  const aria = submitAriaLabel ?? 'Send';
  const title = submitTitle ?? submitAriaLabel ?? 'Send';

  return (
    <Group
      ref={rootRef}
      attached
      width="100%"
      minW={0}
      display="grid"
      gridTemplateColumns={gridTemplateColumns}
      alignItems="stretch"
    >
      <Textarea
        ref={textareaRef}
        data-chat-composer-text="true"
        minW={0}
        w="100%"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={autoGrow ? 1 : 2}
        resize="none"
        minH="40px"
        maxH={autoGrow ? undefined : '120px'}
        overflowY={autoGrow ? 'hidden' : undefined}
        size="sm"
        fontSize="16px"
        disabled={disabled}
        bg="var(--input-base-background-color)"
        borderColor="var(--border-base-color)"
        borderWidth="var(--border-base-width)"
      />
      {trailingActions}
      {modelPicker}
      <Button
        type="button"
        size="sm"
        colorScheme="blue"
        variant="solid"
        onClick={() => void onSubmit()}
        disabled={submitButtonDisabled}
        loading={loading}
        minW="44px"
        px={3}
        py={0}
        aria-label={aria}
        title={title}
        lineHeight={1}
        style={{
          height: '100%',
          minHeight: '100%',
          alignSelf: 'stretch',
        }}
      >
        {submitIcon ?? <FiArrowUp size={18} strokeWidth={2.5} aria-hidden />}
      </Button>
    </Group>
  );
}
